import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { useSetAtom } from 'jotai';
import { useHistory } from 'react-router';

import type { ClaimWritebackStatus } from '@/react-query/useWritebackStatus';

import type { TableColumn } from '../../../common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '../../../common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '../../../common-components/elements/StatusChip/StatusChip';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { type Appointment } from '../../../react-query/useAppointments';
import { getProviderName } from '../../patientList/huddlePrintoutContent/helpers';
import { PatientInfo } from '../../patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import type { TTreatmentResponse } from '../freeClaims/insuranceExport/types';
import {
  cleanClaimsAttachmentValuesAtom,
  useLocalizedFormatDate,
} from '../freeClaims/state';

import { GenerateClaimAttachmentButton } from './claim/GenerateClaimAttachmentButton';
import {
  buildClaimPath,
  getClaimStatusForAppointment,
  getDisplayName,
  normalizeEstimatedVisitId,
  normalizeImagingPatientId,
} from './helpers';
import { useClaimsSorting } from './hooks/useClaimsSorting';
import { ClaimTableStatus } from './types';

interface ClaimsTableProps {
  appointments: (Appointment & {
    treatments: TTreatmentResponse[];
    allTreatmentsExcluded: boolean;
    patientName?: string;
    providerName?: string;
    dateOfService?: string;
    payerName?: string;
    payerId?: number;
    writebackStatus?: ClaimWritebackStatus;
  })[];
  selectedRows?: string[];
  isSearchActive?: boolean;
  onSetSelectedRows: (rows: string[]) => void;
  scrollContainerRef?: React.RefCallback<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
}

export const ClaimsTable = ({
  appointments,
  isSearchActive = false,
  scrollContainerRef,
  sentinelRef,
}: ClaimsTableProps) => {
  // atoms
  const setCleanClaimsAttachmentValues = useSetAtom(
    cleanClaimsAttachmentValuesAtom,
  );

  // hooks
  const history = useHistory();
  const locale = useGetDateFNSLocale();
  const formatDate = useLocalizedFormatDate();

  // Sorting logic
  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useClaimsSorting(appointments, isSearchActive);

  // Define columns for SortableTable
  const columns: TableColumn<
    Appointment & {
      treatments: TTreatmentResponse[];
      allTreatmentsExcluded: boolean;
      patientName?: string;
      providerName?: string;
      dateOfService?: string;
      payerName?: string;
      payerId?: number;
      writebackStatus?: ClaimWritebackStatus;
    }
  >[] = [
    {
      key: 'patient',
      header: <Trans>Patient</Trans>,
      width: 'vai-w-52',
      isSortable: true,
      render: (appointment, index) => (
        <PatientInfo
          context={PatientListPageLayoutContextMap.Claims}
          appointment={appointment}
          index={index}
        />
      ),
    },
    {
      key: 'appointment',
      header: <Trans>Service Time</Trans>,
      width: 'vai-w-36',
      isSortable: true,
      render: (appointment) => (
        <>
          {appointment.dateOfService != null
            ? formatDate(
                appointment.dateOfService,
                false,
                isSearchActive ? 'P' : 'h:mmaaa',
              )
            : isSearchActive
              ? formatDate(appointment.appointment.start, false, 'P')
              : formatDate(appointment.appointment.start, false, 'h:mmaaa')}
        </>
      ),
    },
    {
      key: 'provider',
      header: <Trans>Provider Name</Trans>,
      width: 'vai-w-52',
      isSortable: true,
      render: (appointment) => (
        <>
          {(appointment.providerName ??
            `${appointment.appointment.provider?.firstName ?? ''} ${appointment.appointment.provider?.lastName ?? ''}`.trim()) ||
            '—'}
        </>
      ),
    },
    {
      key: 'primaryPayer',
      header: <Trans>Primary Payer</Trans>,
      width: 'vai-w-52',
      isSortable: true,
      render: (appointment) => <>{appointment.payerName || ''}</>,
    },
    {
      key: 'serviceCodes',
      header: <Trans>Service Codes</Trans>,
      width: 'vai-w-64',
      render: (appointment) => {
        const sortedTreatments = appointment.treatments || [];
        return (
          <div className="vai-flex vai-gap-1 vai-flex-wrap">
            {[...new Set(sortedTreatments.map((t) => t.txCode))].map(
              (txCode) => (
                <div
                  key={txCode}
                  className="vai-bg-slate-200 vai-text-zinc-900 vai-rounded-full vai-whitespace-nowrap vai-px-3 vai-py-1 vai-text-center"
                >
                  {txCode}
                </div>
              ),
            )}
          </div>
        );
      },
    },
    {
      key: 'services',
      header: <Trans>Services</Trans>,
      render: (appointment) => (
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {[
              // Get unique service names from treatments
              ...new Set(
                appointment.treatments
                  .map(
                    // Get the service name
                    (t) => t.txGroupName || '',
                  )
                  // Remove potential empty strings resulting from missing translations
                  .filter(Boolean),
              ),
            ].join(', ')}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: <Trans>Status</Trans>,
      isSortable: true,
      render: (appointment) => {
        const claimStatus = getClaimStatusForAppointment(appointment);
        switch (claimStatus) {
          case ClaimTableStatus.SUBMITTED:
            return (
              <StatusChip
                text={<Trans>Submitted</Trans>}
                variant="success"
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                backgroundColor="vai-bg-green-600"
                textColor="vai-text-white"
                iconColor="vai-text-white"
              />
            );
          case ClaimTableStatus.READY:
            return (
              <StatusChip
                text={<Trans>Ready to Submit</Trans>}
                variant="success"
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
              />
            );
          case ClaimTableStatus.REVIEW:
            return (
              <StatusChip
                text={<Trans>Needs Review</Trans>}
                variant="warning"
                icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
              />
            );
          case ClaimTableStatus.NO_ATTACHMENT:
            return (
              <span className="vai-text-muted-foreground">
                <Trans>No Attachment Needed</Trans>
              </span>
            );
        }
      },
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[100px]',
      render: (appointment) => {
        if (appointment.allTreatmentsExcluded) {
          return null;
        }
        return (
          <div className="vai-flex vai-justify-end vai-gap-2">
            <GenerateClaimAttachmentButton
              onClick={() => handleClickExport(appointment)}
              appointment={appointment}
              variant={'outline'} // show outline according to clean claims 4.0
            />
          </div>
        );
      },
    },
  ];

  // handlers
  const handleClickExport = (
    appointment: Appointment & {
      patientName?: string;
      providerName?: string;
      dateOfService?: string;
    },
  ) => {
    const patientName =
      appointment.patientName ?? getDisplayName(appointment, 0).patientName;
    const providerName =
      appointment.providerName ??
      getProviderName(appointment?.appointment.provider);
    const dateOfVisit =
      appointment.dateOfService != null
        ? formatDate(appointment.dateOfService, false)
        : formatDate(appointment?.appointment.start || '');
    const estimatedVisitId = normalizeEstimatedVisitId(
      appointment.appointment.estimatedVisitId,
    );
    const imagingPatientId = normalizeImagingPatientId(
      appointment.patient?.imagingPatientVideaId,
    );
    setCleanClaimsAttachmentValues({
      unifiedId: String(appointment.appointment.unifiedId),
      estimatedVisitId: estimatedVisitId?.toString(),
      patientInfo: {
        name: patientName,
        dob: formatDate(appointment.patient.dob ?? '', true),
        dateOfVisit,
        providerName,
      },
      locale,
    });
    history.push(
      buildClaimPath(
        appointment.appointment.unifiedId,
        estimatedVisitId,
        imagingPatientId,
      ),
    );
  };

  return (
    <SortableTable
      data={sortedAppointments}
      columns={columns}
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={handleSort}
      rowKey={(appointment) => `claim-${appointment.appointment.unifiedId}`}
      className="vai-min-h-0 vai-flex-1"
      scrollRef={scrollContainerRef}
      sentinelRef={sentinelRef}
    />
  );
};
